import { Router } from '@lightningjs/sdk'

import {
  NavigationPointers,
  PointerOrientation,
  VerticalPointerSize,
} from '../../components/NavigationPointers/NavigationPointers'
import TvGridLayout from '../../components/TvGridLayout/TvGridLayout'
import { getColor, grey960 } from '../../constants/colors'
import { LEFT_SAFE_ZONE_MARGIN } from '../../constants/general'
import { AppConfig } from '../../services/appConfig'
import { TvGridContainerItem } from '../../types/flo-experience'
import { captureScreenData } from '../../utils/segmentAnalytics'
import PageHandler from './pageHandler'

export default class GridHandler extends PageHandler {
  protected _lastYOffset: number
  protected _lastFocusedRow: number
  protected _lastFocusedTile: number
  protected _lastGridItems: TvGridContainerItem[] = []
  protected _lastPageNumber: number
  protected _isSportPicker: boolean = false

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      collision: true,
      color: getColor(grey960),
      TvGridLayout: {
        x: LEFT_SAFE_ZONE_MARGIN,
        type: TvGridLayout,
      },
      NavigationPointers: {
        type: NavigationPointers,
        pointerOrientation: PointerOrientation.VERTICAL,
        verticalPointerSize: VerticalPointerSize.SMALL,
      },
    }
  }

  static _states() {
    return [
      class NotReady extends this {
        $enter() {
          this.setLoadingAnimation(true)
        }

        _handleKey() {
          return true
        }

        $exit() {
          this.setLoadingAnimation(false)
          void captureScreenData(this.tvGridLayout.data?.analytics)
        }
      },
      class Ready extends this {
        $enter() {
          this.checkNavigationPointersVisibility()
          Router.focusPage()
        }

        _getFocused() {
          return this.tvGridLayout
        }
      },
      class InBackgroundByModal extends this {
        _handleKey() {
          this.focusActiveModal()
        }
      },
    ]
  }

  get tvGridLayout() {
    return this.tag('TvGridLayout') as TvGridLayout
  }

  get navigationPointers() {
    return this.tag('NavigationPointers') as NavigationPointers
  }

  get lastGridItems() {
    return this.tvGridLayout.tvGridContainer?.lastGridItems
  }

  get currentRow() {
    return this.tvGridLayout.tvGridContainer?.currentRow
  }

  set currentRow(rowIndex: number) {
    this.tvGridLayout.tvGridContainer.currentRow = rowIndex
  }

  get currentTile() {
    return this.tvGridLayout.tvGridContainer?.currentChild?.currentTile
  }

  set currentTile(tileIndex: number) {
    if (this.tvGridLayout.tvGridContainer?.currentChild?.currentTile !== null) {
      this.tvGridLayout.tvGridContainer.currentChild.currentTile = tileIndex
    }
  }

  historyState(params) {
    if (params) {
      this._lastYOffset = params.previous_y_offset
      this._lastFocusedRow = params.previous_row ?? 0
      this._lastFocusedTile = params.previous_tile ?? 0
      this._lastGridItems = params.previous_items ?? []
      this._lastPageNumber = params.previous_page_number
    } else {
      return {
        previous_y_offset: this.tvGridLayout.tvGridContainer.y,
        previous_row: this.currentRow,
        previous_tile: this.currentTile,
        previous_items: this.lastGridItems,
        previous_page_number: this.tvGridLayout.tvGridContainer.pageNumber,
      }
    }
  }

  $upMovement() {
    this.adjustPointerUpMovement()
    this.tvGridLayout?.tvGridContainer?._handleUp()
    this.checkNavigationPointersVisibility()
  }

  adjustPointerUpMovement() {
    const totalElements = this.tvGridLayout?.tvGridContainer?.totalEl
    const isFirstRowFocused = this.tvGridLayout?.tvGridContainer?.isFocusingFirstRow
    const isLastRowFocused = this.tvGridLayout?.tvGridContainer?.isFocusingLastRow
    const currentTile = this.tvGridLayout?.tvGridContainer?.currentChild?.currentTile

    if (this._isSportPicker && isLastRowFocused) {
      this.tvGridLayout.tvGridContainer.currentRow -= 1
      this.tvGridLayout.tvGridContainer.currentChild.currentTile = currentTile
    } else if (this._isSportPicker && isFirstRowFocused && totalElements === 3) {
      // Grid was not scrolling appropriately after hovering an item from the first column on sports pickers with 3 rows only
      this.tvGridLayout?.tvGridContainer?.reset()
    }
  }

  $downMovement() {
    this.adjustPointerDownMovement()
    this.tvGridLayout?.tvGridContainer?._handleDown()
    this.checkNavigationPointersVisibility()
  }

  adjustPointerDownMovement() {
    const isFirstRowFocused = this.tvGridLayout?.tvGridContainer?.isFocusingFirstRow
    const currentTile = this.tvGridLayout?.tvGridContainer?.currentChild?.currentTile

    if (this._isSportPicker && isFirstRowFocused) {
      this.tvGridLayout.tvGridContainer.currentRow += 1
      this.tvGridLayout.tvGridContainer.currentChild.currentTile = currentTile
    }
  }

  $nextPageLoadComplete() {
    this.checkNavigationPointersVisibility()
  }

  checkNavigationPointersVisibility() {
    const { isCursorVisible } = AppConfig.shared.appSettings
    const totalElements = this.tvGridLayout?.tvGridContainer?.totalEl

    const isFirstRowFocused = this.tvGridLayout?.tvGridContainer?.isFocusingFirstRow
    const isSecondRowFocused = this.currentRow === 1
    const isSecondToLastRowFocused = this.currentRow === totalElements - 2
    const isLastRowFocused = this.tvGridLayout?.tvGridContainer?.isFocusingLastRow

    const showTopPointer = this._isSportPicker
      ? !(isFirstRowFocused || isSecondRowFocused)
      : !isFirstRowFocused
    const showBottomPointer = this._isSportPicker
      ? !(isSecondToLastRowFocused || isLastRowFocused)
      : !isLastRowFocused

    const hasHeader = this.tvGridLayout?.tvGridContainer?.hasHeader
    if (hasHeader) {
      this.tvGridLayout.title.visible = !showTopPointer || !isCursorVisible
      this.tvGridLayout.pointerBackButton.visible = isCursorVisible
    }

    this.adjustNavigationPointersCollisionBox()

    this.navigationPointers.visible = isCursorVisible
    this.navigationPointers.showTopPointer = showTopPointer && isCursorVisible
    this.navigationPointers.showBottomPointer = showBottomPointer && isCursorVisible
    this.navigationPointers.bottomContainer.collision = showBottomPointer && isCursorVisible

    this._refocus()
  }

  _handleScroll({ deltaY }) {
    const isGoingDown = deltaY > 0
    const isGoingUp = !isGoingDown

    if (isGoingDown) {
      this.adjustPointerDownMovement()
    } else {
      this.adjustPointerUpMovement()
    }

    const totalElements = this.tvGridLayout?.tvGridContainer?.totalEl
    const isSecondRowFocused = this.currentRow === 1
    const isSecondToLastRowFocused = this.currentRow === totalElements - 2

    if (
      !this._isSportPicker ||
      (isGoingDown && !isSecondToLastRowFocused) ||
      (isGoingUp && !isSecondRowFocused) ||
      totalElements === 3
    ) {
      this.tvGridLayout?.tvGridContainer?.handleScroll({ deltaY })
    }

    this.checkNavigationPointersVisibility()
  }

  $updateFocusByHover({ rowIndex, cardIndex }) {
    this.currentRow = rowIndex
    this.currentTile = cardIndex
    this._refocus()
  }

  // Increases the height of elements that overflow the navigation pointers that prevent
  // the user from clicking the tiles, even when not being fully covered by the gradients,
  // for some very specific tile combinations on grids
  adjustNavigationPointersCollisionBox() {
    const hasHeader = this.tvGridLayout?.tvGridContainer?.hasHeader
    const totalElements = this.tvGridLayout?.tvGridContainer?.totalEl
    const isFirstRowFocused = this.tvGridLayout?.tvGridContainer?.isFocusingFirstRow
    const isSecondRowFocused = this.currentRow === 1
    const isSecondToLastRowFocused = this.currentRow === totalElements - 2
    const isLastRowFocused = this.tvGridLayout?.tvGridContainer?.isFocusingLastRow
    const isFocusInTheMiddle =
      !isFirstRowFocused && !isSecondRowFocused && !isLastRowFocused && !isSecondToLastRowFocused

    if (this._isSportPicker) {
      if (hasHeader && (isFirstRowFocused || isSecondRowFocused)) {
        this.navigationPointers.topOverflowCollisionBox.h = 0
        this.navigationPointers.bottomOverflowCollisionBox.h = 100
      } else if (hasHeader && (isLastRowFocused || isSecondToLastRowFocused)) {
        this.navigationPointers.topOverflowCollisionBox.h = 50
        this.navigationPointers.bottomOverflowCollisionBox.h = 0
      } else if (hasHeader && isFocusInTheMiddle) {
        this.navigationPointers.topOverflowCollisionBox.h = 100
        this.navigationPointers.bottomOverflowCollisionBox.h = 120
      } else {
        // Sports screen
        this.navigationPointers.topOverflowCollisionBox.h = 0
        this.navigationPointers.bottomOverflowCollisionBox.h = 0
      }

      return
    }

    // See all screens
    if (isFirstRowFocused) {
      this.navigationPointers.topOverflowCollisionBox.h = 0
      this.navigationPointers.bottomOverflowCollisionBox.h = 150
    } else if (isLastRowFocused) {
      this.navigationPointers.topOverflowCollisionBox.h = 300
      this.navigationPointers.bottomOverflowCollisionBox.h = 0
    } else {
      this.navigationPointers.topOverflowCollisionBox.h = 215
      this.navigationPointers.bottomOverflowCollisionBox.h = 0
    }
  }

  _onCursorEnter() {
    this.checkNavigationPointersVisibility()
  }

  _onCursorLeave() {
    this.checkNavigationPointersVisibility()
    this.tvGridLayout?.tvGridContainer?.currentChild?.currentChild?._focus()
  }
}
